import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { AppLayout } from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  Loader2, Zap, CreditCard, Calendar, CheckCircle2, ArrowRight,
  Sparkles, Infinity as InfinityIcon,
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return null;
  }
};

const BillingSettings = () => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [canceling, setCanceling] = useState(false);

  const load = async () => {
    try {
      const [statusRes, plansRes] = await Promise.all([
        fetch(`${API}/subscription/status`, { headers: getAuthHeaders() }),
        fetch(`${API}/subscription/plans`, { headers: getAuthHeaders() }),
      ]);
      if (statusRes.ok) {
        const s = await statusRes.json();
        setStatus(s);
      }
      if (plansRes.ok) {
        const plans = await plansRes.json();
        const current = plans.find((p) => p.is_current) || plans.find((p) => p.id === 'free');
        setPlan(current);
      }
    } catch (e) {
      process.env.NODE_ENV === 'development' && console.error('Billing load error:', e);
      toast.error('Could not load your billing details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCancel = async () => {
    setCanceling(true);
    try {
      const res = await fetch(`${API}/subscription/cancel`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('cancel failed');
      toast.success('Subscription canceled. You are now on the Free plan.');
      await load();
    } catch (e) {
      toast.error('Failed to cancel subscription. Please try again.');
    } finally {
      setCanceling(false);
    }
  };

  if (loading) {
    return (
      <AppLayout maxWidth="max-w-3xl">
        <div className="py-20 flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-teal-500" />
        </div>
      </AppLayout>
    );
  }

  const isFree = !status || status.plan_id === 'free';
  const isUnlimited = status && status.monthly_generations === -1;
  const interval = plan?.billing_interval; // 'monthly' | 'annual' | null
  const price = plan
    ? interval === 'annual'
      ? plan.annual_price
      : plan.monthly_price ?? plan.price
    : 0;
  const renewalDate = formatDate(status?.current_period_end);

  return (
    <AppLayout maxWidth="max-w-3xl">
      <Toaster position="top-center" richColors />

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6"
      >
        <div className="flex items-center gap-2 mb-1">
          <CreditCard className="h-5 w-5 text-teal-600" />
          <h1 className="text-2xl font-bold tracking-tight text-foreground" data-testid="billing-title">
            Billing & Plan
          </h1>
        </div>
        <p className="text-sm text-muted-foreground">
          Review your plan, usage, and manage your subscription.
        </p>
      </motion.div>

      {/* Current plan card */}
      <Card className="border-border border-t-4 border-t-teal-500 overflow-hidden" data-testid="billing-current-plan">
        <CardContent className="p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Current plan
                </span>
                {status?.is_active ? (
                  <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-0" data-testid="billing-status-badge">
                    Active
                  </Badge>
                ) : (
                  <Badge variant="secondary" data-testid="billing-status-badge">Inactive</Badge>
                )}
              </div>
              <div className="flex items-baseline gap-2">
                <h2 className="text-2xl font-bold text-foreground" data-testid="billing-plan-name">
                  {status?.plan_name || 'Free'}
                </h2>
                {!isFree && (
                  <span className="text-muted-foreground text-sm">
                    ${price}
                    {interval === 'annual' ? '/year' : '/month'}
                  </span>
                )}
              </div>
              {!isFree && interval && (
                <p className="text-xs text-muted-foreground mt-1 capitalize">
                  Billed {interval}
                </p>
              )}
            </div>

            <div className="text-right">
              {isFree ? (
                <Button
                  onClick={() => navigate('/pricing')}
                  className="bg-creataby-gradient-btn text-white hover:opacity-95"
                  data-testid="billing-upgrade-btn"
                >
                  <Zap className="h-4 w-4 mr-2" />
                  Upgrade
                </Button>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => navigate('/pricing')}
                  className="border-border"
                  data-testid="billing-change-plan-btn"
                >
                  Change plan
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>
          </div>

          {/* Meta rows */}
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            {/* Usage */}
            <div className="rounded-lg border border-border p-4" data-testid="billing-usage">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                Generations this month
              </p>
              {isUnlimited ? (
                <p className="text-lg font-bold text-foreground flex items-center gap-1.5">
                  <InfinityIcon className="h-5 w-5 text-teal-600" />
                  Unlimited
                </p>
              ) : (
                <p className="text-lg font-bold text-foreground">
                  {status?.generations_used ?? 0}
                  <span className="text-sm font-normal text-muted-foreground">
                    {' '}/ {status?.monthly_generations ?? 2} used
                  </span>
                </p>
              )}
            </div>

            {/* Renewal / active */}
            <div className="rounded-lg border border-border p-4" data-testid="billing-renewal">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                {isFree ? 'Plan' : 'Renewal'}
              </p>
              <p className="text-sm font-medium text-foreground flex items-center gap-1.5">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                {isFree
                  ? 'Free forever — no billing'
                  : renewalDate
                  ? `Renews ${renewalDate}`
                  : 'Active — no scheduled end date'}
              </p>
            </div>
          </div>

          {/* Plan perks quick list */}
          {plan?.entitlements && (
            <div className="mt-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                What's included
              </p>
              <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-1.5">
                {[
                  plan.entitlements.max_active_projects === -1
                    ? 'Unlimited active projects'
                    : `${plan.entitlements.max_active_projects} active project${plan.entitlements.max_active_projects === 1 ? '' : 's'}`,
                  isUnlimited ? 'Unlimited generations' : `${status?.monthly_generations ?? 2} generations / month`,
                  plan.entitlements.can_export_pdf ? 'PDF export' : 'Export to funnel builders',
                  plan.entitlements.can_export_to_platform ? 'ClickFunnels & Systeme.io export' : 'All 3 income paths',
                ].map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-foreground/90">
                    <CheckCircle2 className="h-4 w-4 text-teal-600 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manage subscription (cancel) */}
      {!isFree && (
        <Card className="border-border mt-5" data-testid="billing-manage-card">
          <CardContent className="p-6 flex flex-wrap items-center justify-between gap-4">
            <div>
              <h3 className="font-semibold text-foreground">Cancel subscription</h3>
              <p className="text-sm text-muted-foreground mt-0.5 max-w-md">
                You'll move to the Free plan (2 generations/month, 1 active project). Your saved work is never deleted.
              </p>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  className="border-destructive/40 text-destructive hover:bg-destructive/5"
                  data-testid="billing-cancel-btn"
                >
                  Cancel plan
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="billing-cancel-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Cancel your subscription?</AlertDialogTitle>
                  <AlertDialogDescription>
                    You'll be moved to the Free plan right away. You can upgrade again at any time — your projects and saved outputs stay safe.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="billing-cancel-dismiss">Keep my plan</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleCancel}
                    disabled={canceling}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    data-testid="billing-cancel-confirm"
                  >
                    {canceling ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Canceling...
                      </>
                    ) : (
                      'Yes, cancel'
                    )}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>
      )}

      {/* Free plan upsell */}
      {isFree && (
        <Card className="border-border mt-5 bg-gradient-to-br from-teal-50 to-emerald-50/50" data-testid="billing-upsell-card">
          <CardContent className="p-6 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <Sparkles className="h-5 w-5 text-teal-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-foreground">Ready for more?</h3>
                <p className="text-sm text-muted-foreground mt-0.5 max-w-md">
                  Upgrade to Creator for unlimited generations and up to 3 active projects — cancel anytime.
                </p>
              </div>
            </div>
            <Button
              onClick={() => navigate('/pricing')}
              className="bg-creataby-gradient-btn text-white hover:opacity-95"
              data-testid="billing-see-plans-btn"
            >
              See plans
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      )}

      <p className="text-center text-xs text-muted-foreground mt-6">
        Questions about billing? Email{' '}
        <a href="mailto:support@creataby.com" className="underline hover:text-foreground">support@creataby.com</a>
      </p>
    </AppLayout>
  );
};

export default BillingSettings;
